/**
 * offlineStorage.js — IndexedDB-backed offline storage for Field Operations.
 *
 * Stores:
 *   - GPS position queue (positions captured while offline, flushed when online)
 *   - SP points cache (last-fetched SP data per project)
 *   - Personnel locations cache (last-fetched positions per project)
 *
 *   - Field Work Planning snapshot per project (v5)
 *
 * Uses raw IndexedDB (no library) to keep bundle size minimal.
 */

const DB_NAME = 'archon_offline';
const DB_VERSION = 5;

// Store names
const GPS_QUEUE = 'gps_queue';
const SP_CACHE = 'sp_cache';
const PERSONNEL_CACHE = 'personnel_cache';
const ROADS_CACHE = 'roads_cache';
const CREW_REPORT_DRAFTS = 'crew_report_drafts';
const SP_CACHE_CREW = 'sp_cache_crew';
const FUEL_LOG_DRAFTS = 'fuel_log_drafts';
const FUEL_CONSUMERS_CACHE = 'fuel_consumers_cache';
const FIELD_PLANNING_CACHE = 'field_planning_cache';
export const OFFLINE_FIELD_DATA_TTL_MS = 24 * 60 * 60 * 1000; // 24 hours

let dbPromise = null;

export function isOfflineDataFresh(cachedAt, ttlMs = OFFLINE_FIELD_DATA_TTL_MS) {
  const ts = Number(cachedAt || 0);
  if (!Number.isFinite(ts) || ts <= 0) return false;
  return Date.now() - ts <= ttlMs;
}

function openDB() {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      // GPS queue: auto-increment key, indexed by timestamp
      if (!db.objectStoreNames.contains(GPS_QUEUE)) {
        const store = db.createObjectStore(GPS_QUEUE, { keyPath: 'id', autoIncrement: true });
        store.createIndex('timestamp', 'timestamp', { unique: false });
      }
      // SP cache: keyed by project_id
      if (!db.objectStoreNames.contains(SP_CACHE)) {
        db.createObjectStore(SP_CACHE, { keyPath: 'project_id' });
      }
      // Personnel cache: keyed by project_id
      if (!db.objectStoreNames.contains(PERSONNEL_CACHE)) {
        db.createObjectStore(PERSONNEL_CACHE, { keyPath: 'project_id' });
      }
      // Roads cache: keyed by project_id (v2)
      if (!db.objectStoreNames.contains(ROADS_CACHE)) {
        db.createObjectStore(ROADS_CACHE, { keyPath: 'project_id' });
      }
      // Crew report drafts: local drafts for field module (v3)
      if (!db.objectStoreNames.contains(CREW_REPORT_DRAFTS)) {
        const draftsStore = db.createObjectStore(CREW_REPORT_DRAFTS, { keyPath: 'id', autoIncrement: true });
        draftsStore.createIndex('project_id', 'project_id', { unique: false });
        draftsStore.createIndex('synced', 'synced', { unique: false });
      }
      // SP/lines cache for crew report form (offline line + SP lists) (v3)
      if (!db.objectStoreNames.contains(SP_CACHE_CREW)) {
        db.createObjectStore(SP_CACHE_CREW, { keyPath: 'project_id' });
      }
      // Fuel log drafts: supervisor fuel entries saved offline (v4)
      if (!db.objectStoreNames.contains(FUEL_LOG_DRAFTS)) {
        const fuelStore = db.createObjectStore(FUEL_LOG_DRAFTS, { keyPath: 'id', autoIncrement: true });
        fuelStore.createIndex('project_id', 'project_id', { unique: false });
        fuelStore.createIndex('synced', 'synced', { unique: false });
        fuelStore.createIndex('log_date', 'log_date', { unique: false });
      }
      // Fuel consumers cache: equipment list for offline use (v4)
      if (!db.objectStoreNames.contains(FUEL_CONSUMERS_CACHE)) {
        db.createObjectStore(FUEL_CONSUMERS_CACHE, { keyPath: 'project_id' });
      }
      // Field Work Planning: last synced snapshot per project (v5)
      if (!db.objectStoreNames.contains(FIELD_PLANNING_CACHE)) {
        db.createObjectStore(FIELD_PLANNING_CACHE, { keyPath: 'project_id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });

  return dbPromise;
}

// ─── GPS Queue ──────────────────────────────────────────────────────────────

/**
 * Enqueue a GPS position for later sync.
 * @param {Object} locationData - { latitude, longitude, accuracy, altitude, heading, speed, project_id }
 */
export async function enqueueGPSPosition(locationData) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(GPS_QUEUE, 'readwrite');
    tx.objectStore(GPS_QUEUE).add({
      ...locationData,
      timestamp: Date.now(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get all queued GPS positions (oldest first).
 * @returns {Promise<Array>}
 */
export async function getQueuedPositions() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(GPS_QUEUE, 'readonly');
    const request = tx.objectStore(GPS_QUEUE).index('timestamp').getAll();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remove a batch of positions by their IDs after successful send.
 * @param {number[]} ids
 */
export async function removeQueuedPositions(ids) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(GPS_QUEUE, 'readwrite');
    const store = tx.objectStore(GPS_QUEUE);
    for (const id of ids) {
      store.delete(id);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Count pending GPS positions in queue.
 * @returns {Promise<number>}
 */
export async function getQueuedCount() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(GPS_QUEUE, 'readonly');
    const request = tx.objectStore(GPS_QUEUE).count();
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

// ─── SP Points Cache ────────────────────────────────────────────────────────

/**
 * Cache SP points data for a project.
 * @param {number} projectId
 * @param {Object} spData - Full SP data object from API
 * @param {?string} etag - Sello del servidor, para poder pedir con
 *   `If-None-Match` en la siguiente consulta y recibir 304 sin cuerpo.
 */
export async function cacheSPData(projectId, spData, etag = null) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SP_CACHE, 'readwrite');
    tx.objectStore(SP_CACHE).put({
      project_id: projectId,
      data: spData,
      etag,
      cached_at: Date.now(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get cached SP data for a project.
 * @param {number} projectId
 * @returns {Promise<{data: Object, etag: ?string, cached_at: number}|null>}
 */
export async function getCachedSPData(projectId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SP_CACHE, 'readonly');
    const request = tx.objectStore(SP_CACHE).get(projectId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

// ─── Personnel Locations Cache ──────────────────────────────────────────────

/**
 * Cache personnel location data for a project.
 * @param {number} projectId
 * @param {Array} personnel
 */
export async function cachePersonnelData(projectId, personnel) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PERSONNEL_CACHE, 'readwrite');
    tx.objectStore(PERSONNEL_CACHE).put({
      project_id: projectId,
      data: personnel,
      cached_at: Date.now(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get cached personnel data for a project.
 * @param {number} projectId
 * @returns {Promise<{data: Array, cached_at: number}|null>}
 */
export async function getCachedPersonnelData(projectId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(PERSONNEL_CACHE, 'readonly');
    const request = tx.objectStore(PERSONNEL_CACHE).get(projectId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

// ─── Roads Cache ────────────────────────────────────────────────────────────

/**
 * Cache road track data for a project.
 * @param {number} projectId
 * @param {Array} roads
 */
export async function cacheRoadsData(projectId, roads) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ROADS_CACHE, 'readwrite');
    tx.objectStore(ROADS_CACHE).put({
      project_id: projectId,
      data: roads,
      cached_at: Date.now(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get cached road track data for a project.
 * @param {number} projectId
 * @returns {Promise<{data: Array, cached_at: number}|null>}
 */
export async function getCachedRoadsData(projectId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(ROADS_CACHE, 'readonly');
    const request = tx.objectStore(ROADS_CACHE).get(projectId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

// ─── Crew Report Drafts (field module offline) ───────────────────────────────

/**
 * Save or update a crew report draft. Payload matches API create shape: project_id, crew_name, report_date, supervisor_id, supervisor_name, work_hours, water_use, bentonite_use, workers_count, notes, holes.
 * @param {Object} draft - { project_id, crew_name, report_date, ...holes } and optional id for update
 * @param {boolean} [synced=false]
 * @returns {Promise<number>} stored id
 */
export async function saveCrewReportDraft(draft, synced = false) {
  const db = await openDB();
  const record = {
    ...draft,
    synced: !!synced,
    updated_at: Date.now(),
  };
  if (record.id != null) {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(CREW_REPORT_DRAFTS, 'readwrite');
      tx.objectStore(CREW_REPORT_DRAFTS).put(record);
      tx.oncomplete = () => resolve(record.id);
      tx.onerror = () => reject(tx.error);
    });
  }
  delete record.id;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CREW_REPORT_DRAFTS, 'readwrite');
    const req = tx.objectStore(CREW_REPORT_DRAFTS).add(record);
    tx.oncomplete = () => resolve(req.result);
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get all crew report drafts, optionally filtered by project_id or synced.
 * @param {{ project_id?: number, synced?: boolean }} [filter]
 * @returns {Promise<Array>}
 */
export async function getCrewReportDrafts(filter = {}) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CREW_REPORT_DRAFTS, 'readonly');
    const req = tx.objectStore(CREW_REPORT_DRAFTS).getAll();
    req.onsuccess = () => {
      let list = req.result || [];
      if (filter.project_id != null) list = list.filter((d) => d.project_id === filter.project_id);
      if (filter.synced != null) list = list.filter((d) => d.synced === filter.synced);
      list.sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0));
      resolve(list);
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Get one draft by id.
 * @param {number} id
 * @returns {Promise<Object|null>}
 */
export async function getCrewReportDraft(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CREW_REPORT_DRAFTS, 'readonly');
    const request = tx.objectStore(CREW_REPORT_DRAFTS).get(id);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete a crew report draft by id.
 * @param {number} id
 */
export async function deleteCrewReportDraft(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CREW_REPORT_DRAFTS, 'readwrite');
    tx.objectStore(CREW_REPORT_DRAFTS).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Mark a draft as synced (or remove it after successful sync; caller can delete instead).
 * @param {number} id
 * @param {boolean} synced
 */
export async function setCrewReportDraftSynced(id, synced) {
  const db = await openDB();
  const draft = await getCrewReportDraft(id);
  if (!draft) return;
  draft.synced = !!synced;
  draft.updated_at = Date.now();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CREW_REPORT_DRAFTS, 'readwrite');
    tx.objectStore(CREW_REPORT_DRAFTS).put(draft);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── SP/Lines cache for crew report form (offline) ───────────────────────────

/**
 * Cache lines, SPs by line, and supervisors for crew report field module.
 * @param {number} projectId
 * @param {{ lines: Array, spsByLine: Object.<string, Array>, supervisors?: Array }} data
 */
export async function cacheCrewSPData(projectId, data) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SP_CACHE_CREW, 'readwrite');
    tx.objectStore(SP_CACHE_CREW).put({
      project_id: projectId,
      lines: data.lines || [],
      spsByLine: data.spsByLine || {},
      supervisors: data.supervisors || [],
      cached_at: Date.now(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get cached lines, SPs and supervisors for crew report form.
 * @param {number} projectId
 * @returns {Promise<{ lines: Array, spsByLine: Object, supervisors?: Array, cached_at: number }|null>}
 */
export async function getCachedCrewSPData(projectId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SP_CACHE_CREW, 'readonly');
    const request = tx.objectStore(SP_CACHE_CREW).get(projectId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remove cached lines / SPs / supervisors for crew report field module (Plan B: stale IndexedDB).
 * Does not delete crew report drafts.
 */
export async function clearCachedCrewSPData(projectId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(SP_CACHE_CREW, 'readwrite');
    tx.objectStore(SP_CACHE_CREW).delete(projectId);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── Fuel Log Drafts (supervisor offline) ─────────────────────────────────────

/**
 * Save a fuel log draft for offline use.
 * @param {Object} draft - { project_id, log_date, fuel_type, quantity_received, ... }
 * @returns {Promise<number>} stored id
 */
export async function saveFuelLogDraft(draft) {
  const db = await openDB();
  const record = {
    ...draft,
    synced: false,
    updated_at: Date.now(),
  };
  if (record.id != null) {
    return new Promise((resolve, reject) => {
      const tx = db.transaction(FUEL_LOG_DRAFTS, 'readwrite');
      tx.objectStore(FUEL_LOG_DRAFTS).put(record);
      tx.oncomplete = () => resolve(record.id);
      tx.onerror = () => reject(tx.error);
    });
  }
  delete record.id;
  return new Promise((resolve, reject) => {
    const tx = db.transaction(FUEL_LOG_DRAFTS, 'readwrite');
    const req = tx.objectStore(FUEL_LOG_DRAFTS).add(record);
    tx.oncomplete = () => resolve(req.result);
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get fuel log drafts, optionally filtered.
 * @param {{ project_id?: number, synced?: boolean }} [filter]
 * @returns {Promise<Array>}
 */
export async function getFuelLogDrafts(filter = {}) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(FUEL_LOG_DRAFTS, 'readonly');
    const req = tx.objectStore(FUEL_LOG_DRAFTS).getAll();
    req.onsuccess = () => {
      let list = req.result || [];
      if (filter.project_id != null) list = list.filter((d) => d.project_id === filter.project_id);
      if (filter.synced != null) list = list.filter((d) => d.synced === filter.synced);
      list.sort((a, b) => (b.updated_at || 0) - (a.updated_at || 0));
      resolve(list);
    };
    req.onerror = () => reject(req.error);
  });
}

/**
 * Mark a fuel log draft as synced.
 * @param {number} id
 */
export async function setFuelLogDraftSynced(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(FUEL_LOG_DRAFTS, 'readonly');
    const request = tx.objectStore(FUEL_LOG_DRAFTS).get(id);
    request.onsuccess = () => {
      const draft = request.result;
      if (!draft) { resolve(); return; }
      draft.synced = true;
      draft.updated_at = Date.now();
      const tx2 = db.transaction(FUEL_LOG_DRAFTS, 'readwrite');
      tx2.objectStore(FUEL_LOG_DRAFTS).put(draft);
      tx2.oncomplete = () => resolve();
      tx2.onerror = () => reject(tx2.error);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete a fuel log draft.
 * @param {number} id
 */
export async function deleteFuelLogDraft(id) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(FUEL_LOG_DRAFTS, 'readwrite');
    tx.objectStore(FUEL_LOG_DRAFTS).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

// ─── Fuel Consumers Cache (equipment list offline) ────────────────────────────

/**
 * Cache the fuel consumers (equipment) list for a project.
 * @param {number} projectId
 * @param {Array} consumers
 */
export async function cacheFuelConsumers(projectId, consumers) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(FUEL_CONSUMERS_CACHE, 'readwrite');
    tx.objectStore(FUEL_CONSUMERS_CACHE).put({
      project_id: projectId,
      data: consumers,
      cached_at: Date.now(),
    });
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Get cached fuel consumers for a project.
 * @param {number} projectId
 * @returns {Promise<Array|null>}
 */
export async function getCachedFuelConsumers(projectId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(FUEL_CONSUMERS_CACHE, 'readonly');
    const request = tx.objectStore(FUEL_CONSUMERS_CACHE).get(projectId);
    request.onsuccess = () => {
      const result = request.result;
      resolve(result ? result.data : null);
    };
    request.onerror = () => reject(request.error);
  });
}

// ─── Field Work Planning cache (read-mostly offline for supervisors) ───────────

/**
 * Replace the planning snapshot for a project (used after merge).
 * @param {number} projectId
 * @param {Object} data
 */
export async function saveFieldPlanningCache(projectId, data) {
  const db = await openDB();
  const record = {
    project_id: projectId,
    cached_at: Date.now(),
    filter_start: data.filter_start ?? null,
    filter_end: data.filter_end ?? null,
    work_groups: Array.isArray(data.work_groups) ? data.work_groups : [],
    plans: Array.isArray(data.plans) ? data.plans : [],
    candidates: Array.isArray(data.candidates) ? data.candidates : [],
    lines_summary: Array.isArray(data.lines_summary) ? data.lines_summary : [],
    pending_sp: (() => {
      const p = data.pending_sp;
      if (p == null) return [];
      if (Array.isArray(p) || (typeof p === 'object' && !Array.isArray(p))) return p;
      return [];
    })(),
    line_registry:
      data.line_registry && typeof data.line_registry === 'object' ? data.line_registry : {},
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(FIELD_PLANNING_CACHE, 'readwrite');
    tx.objectStore(FIELD_PLANNING_CACHE).put(record);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * @param {number} projectId
 * @returns {Promise<Object|null>} full record or null
 */
export async function getCachedFieldPlanningData(projectId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(FIELD_PLANNING_CACHE, 'readonly');
    const request = tx.objectStore(FIELD_PLANNING_CACHE).get(projectId);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Merge partial fields into the existing planning cache (preserves lines / line_registry).
 * @param {number} projectId
 * @param {Object} partial
 */
export async function mergeFieldPlanningCache(projectId, partial) {
  const prev = (await getCachedFieldPlanningData(projectId)) || {};
  const nextRegistry =
    partial.line_registry != null
      ? { ...(prev.line_registry || {}), ...partial.line_registry }
      : (prev.line_registry || {});
  return saveFieldPlanningCache(projectId, {
    filter_start: 'filter_start' in partial ? partial.filter_start : (prev.filter_start ?? null),
    filter_end: 'filter_end' in partial ? partial.filter_end : (prev.filter_end ?? null),
    work_groups: 'work_groups' in partial ? partial.work_groups : (prev.work_groups ?? []),
    plans: 'plans' in partial ? partial.plans : (prev.plans ?? []),
    candidates: 'candidates' in partial ? partial.candidates : (prev.candidates ?? []),
    lines_summary: 'lines_summary' in partial ? partial.lines_summary : (prev.lines_summary ?? []),
    pending_sp: 'pending_sp' in partial ? partial.pending_sp : (prev.pending_sp ?? []),
    line_registry: nextRegistry,
  });
}
